import { Injectable, inject } from '@angular/core';
import { GridCommandManager } from '../commands/grid-command-manager';
import type { GridBridge } from '../interfaces/grid-bridge.interface';
import { GridStateStore } from '../state/grid-state.store';

/**
 * API de alto nível consumida pela tela (via referência de template do Host,
 * ex.: `#grid="companyDataGrid"` -> `grid.facade`). Isola o consumidor dos detalhes
 * internos de Store/Bridge/Commands, expondo apenas operações de negócio do grid.
 *
 * O bridge não é resolvido via `inject()`: ele é um componente renderizado no template
 * do próprio Host (view child), e a árvore de injeção do Angular só resolve tokens
 * subindo para ancestrais — nunca descendo para um view child. Por isso o Host injeta
 * esta Facade normalmente, mas conecta o bridge de forma explícita via `bindBridge()`
 * assim que o `viewChild(NzTableBridgeComponent)` estiver disponível.
 */
@Injectable()
export class GridFacade<T = unknown> {
  private readonly store = inject(GridStateStore<T>);
  private readonly commands = inject(GridCommandManager);
  private bridge: GridBridge<T> | null = null;

  readonly data = this.store.rowData;
  readonly pagedData = this.store.pagedData;
  readonly selectedRows = this.store.selectedData;
  readonly loading = this.store.loading;
  readonly canUndo = this.commands.canUndo;
  readonly canRedo = this.commands.canRedo;

  bindBridge(bridge: GridBridge<T>): void {
    this.bridge = bridge;
  }

  setData(rows: readonly T[]): void {
    this.store.setRowData(rows);
  }

  setLoading(loading: boolean): void {
    this.store.setLoading(loading);
  }

  clearSelection(): void {
    this.store.clearSelection();
    this.bridge?.setSelectedRows([]);
  }

  undo(): void {
    this.commands.undo();
  }

  redo(): void {
    this.commands.redo();
  }

  exportCsv(fileName = 'export'): void {
    this.bridge?.exportToCsv(fileName);
  }

  sizeColumnsToFit(): void {
    this.bridge?.sizeColumnsToFit();
  }
}
